import { scaleTime } from '@visx/scale';
import type { ScaleLinear } from 'd3-scale';
import { useEffect, useRef, useState } from 'react';

import type { Asset } from '@shared/types';

import { DataPoint, DEFAULT_MARGIN, Margin } from './types';

interface UseChartStateProps {
  spotPrice: number;
  strikePrice?: number;
  asset: Asset;
  priceHistory: DataPoint[];
  strikePrices: number[];
}

export function useChartState({
  spotPrice,
  strikePrice,
  asset,
  priceHistory,
  strikePrices,
}: UseChartStateProps) {
  // Simple helper to get a good default view of price and selected strike
  const getDefaultYScale = (
    spotPrice: number,
    strikes: number[],
    selectedStrike?: number,
  ): [number, number] => {
    // Safety check
    if (!spotPrice || spotPrice <= 0) {
      return [95, 105];
    }

    // Figure out a reasonable range based on strike spacing if available
    let range = spotPrice * 0.1; // Fallback to 10% of spot price
    let center = spotPrice;

    if (strikes.length > 1) {
      // Calculate strike spacing
      const sortedStrikes = [...strikes].sort((a, b) => a - b);
      const spacings = [];
      for (let i = 1; i < Math.min(5, sortedStrikes.length); i++) {
        spacings.push(sortedStrikes[i] - sortedStrikes[i - 1]);
      }
      const avgSpacing = spacings.reduce((a, b) => a + b, 0) / spacings.length;
      range = avgSpacing * 2.5;

      // If there's a selected strike, use tight focused view
      if (selectedStrike && selectedStrike > 0) {
        center = selectedStrike;
      }
    }

    const newMin = center - range / 2;
    const newMax = center + range / 2;

    // When a strike is selected, keep the focused tight view
    // Don't expand to include spot price - let the user focus on the selected strike area

    return [newMin, newMax];
  };

  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  const [xFrom, setXFrom] = useState(() => Date.now() - 600000); // Start time in milliseconds (10 minutes)

  // Helper function to create xScale when needed
  const createXScale = (currentTime?: number) => {
    const now = currentTime ?? Date.now();
    const innerWidth =
      dimensions.width - DEFAULT_MARGIN.left - DEFAULT_MARGIN.right;
    return scaleTime({
      domain: [xFrom, now],
      range: [0, innerWidth - 20],
    });
  };

  const [yScale, setYScale] = useState<[number, number]>([0, 0]);
  const [targetYScale, setTargetYScale] = useState<[number, number]>([0, 0]);
  const [previewStrikePrice, setPreviewStrikePrice] = useState<number | null>(
    null,
  );
  const [previewRawPrice, setPreviewRawPrice] = useState<number | null>(null);
  const [previewRawY, setPreviewRawY] = useState<number | null>(null);
  const [nearestValidStrike, setNearestValidStrike] = useState<number | null>(
    null,
  );
  const [smoothedCenterPrice, setSmoothedCenterPrice] = useState(0);
  const [smoothedSpotPrice, setSmoothedSpotPrice] = useState(0);
  const [smoothedYBounds, setSmoothedYBounds] = useState<[number, number]>([
    0, 0,
  ]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastAutoScaleStrike, setLastAutoScaleStrike] = useState<number | null>(
    null,
  );
  const [savedView, setSavedView] = useState<{
    zoom: number;
    bounds: [number, number];
    centerPrice: number;
  } | null>(null);
  const [hasManuallyPanned, setHasManuallyPanned] = useState(false);

  // Refs for interaction state
  const lastTouchDistance = useRef<{ x: number; y: number } | null>(null);
  const lastUpdateTime = useRef<number>(0);
  const isDraggingStrike = useRef<boolean>(false);
  const yScaleRef = useRef<ScaleLinear<number, number, never>>(null);
  const marginRef = useRef<Margin>(DEFAULT_MARGIN);
  const touchStartTime = useRef<number>(0);

  // No need for updateXScaleRange - dimensions changes don't affect xFrom
  // The xScale is created fresh each time using current dimensions and xFrom

  // Reinitialize when asset changes
  useEffect(() => {
    setSmoothedCenterPrice(0);
    setSmoothedSpotPrice(0);
    setIsInitialized(false);
    setHasSetScaleWithStrikes(false);
    setLastStrikePrice(undefined);
    setYScale([0, 0]);
    setTargetYScale([0, 0]);
    setHasManuallyPanned(false);

    // Reset xFrom to default time range (10 minutes ago)
    setXFrom(Date.now() - 600000);
  }, [asset]);

  // Track if we've set the scale with strike data
  const [hasSetScaleWithStrikes, setHasSetScaleWithStrikes] = useState(false);
  // Track the last strike price for comparison
  const [lastStrikePrice, setLastStrikePrice] = useState<number | undefined>(
    strikePrice,
  );

  // Initialize yScale safely and handle updates
  useEffect(() => {
    // If this is the initial load (0, 0) and we have a valid spot price, set default scale
    if (yScale[0] === 0 && yScale[1] === 0 && spotPrice > 0) {
      if (strikePrices.length > 0) {
        setHasSetScaleWithStrikes(true);
      }
      setLastStrikePrice(strikePrice);
      const newScale = getDefaultYScale(spotPrice, strikePrices, strikePrice);
      setYScale(newScale);
      setTargetYScale(newScale);
      return;
    }

    // If we haven't set scale with strike data yet, and now we have strikes, recalculate
    if (!hasSetScaleWithStrikes && strikePrices.length > 0 && spotPrice > 0) {
      setHasSetScaleWithStrikes(true);
      setLastStrikePrice(strikePrice);
      const newScale = getDefaultYScale(spotPrice, strikePrices, strikePrice);
      setTargetYScale(newScale);
      return;
    }

    // If strike price changed, recenter the scale around the new strike
    if (
      strikePrice !== lastStrikePrice &&
      strikePrice &&
      strikePrice > 0 &&
      strikePrices.length > 0
    ) {
      setLastStrikePrice(strikePrice);

      // Use the improved scale calculation that ensures adjacent strikes are visible
      const newScale = getDefaultYScale(spotPrice, strikePrices, strikePrice);
      setTargetYScale(newScale);
      return;
    }

    // If strike was deselected, return to default view centered around spot price
    if (strikePrice !== lastStrikePrice && !strikePrice) {
      setLastStrikePrice(strikePrice);
      const defaultScale = getDefaultYScale(spotPrice, strikePrices, undefined);
      setTargetYScale(defaultScale);
    }
  }, [
    spotPrice,
    strikePrice,
    strikePrices,
    hasSetScaleWithStrikes,
    lastStrikePrice,
    yScale,
  ]);

  // Smooth Y scale transitions
  useEffect(() => {
    if (targetYScale[0] === 0 && targetYScale[1] === 0) {
      return;
    }

    if (yScale[0] === targetYScale[0] && yScale[1] === targetYScale[1]) {
      return;
    }

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const speed = 0.15; // Smooth but responsive

    const interval = setInterval(() => {
      setYScale(current => {
        const newMin = lerp(current[0], targetYScale[0], speed);
        const newMax = lerp(current[1], targetYScale[1], speed);

        // Check if we're close enough to snap to target
        if (
          Math.abs(newMin - targetYScale[0]) < 0.01 &&
          Math.abs(newMax - targetYScale[1]) < 0.01
        ) {
          clearInterval(interval);
          return targetYScale;
        }

        return [newMin, newMax];
      });
    }, 16);

    return () => clearInterval(interval);
  }, [targetYScale, yScale]);

  // Keep spot price centered within middle 20% of chart
  useEffect(() => {
    if (!spotPrice || spotPrice <= 0 || yScale[0] === 0 || yScale[1] === 0) {
      return;
    }

    // Don't auto-center if user has manually panned
    if (hasManuallyPanned) {
      return;
    }

    // Only auto-center when no strike is selected to avoid conflicts
    if (strikePrice && strikePrice > 0) {
      return;
    }

    const [currentMin, currentMax] = yScale;
    const currentRange = currentMax - currentMin;

    // Calculate the middle 60% bounds (20%-80% from bottom)
    const chartMiddle = currentMin + currentRange * 0.5;
    const middleZoneSize = currentRange * 0.3; // 30% on each side = 60% total
    const middleZoneMin = chartMiddle - middleZoneSize;
    const middleZoneMax = chartMiddle + middleZoneSize;

    // Check if spot price is outside the middle 60%
    if (spotPrice < middleZoneMin || spotPrice > middleZoneMax) {
      // Recenter the chart around the spot price
      const newMin = spotPrice - currentRange / 2;
      const newMax = spotPrice + currentRange / 2;

      setTargetYScale([newMin, newMax]);
    }
  }, [spotPrice, yScale, strikePrice, hasManuallyPanned]);

  const updatePreviewStrike = (
    effectivePrice: number,
    y: number,
    nearest: number,
  ) => {
    setPreviewRawPrice(effectivePrice);
    setPreviewRawY(y);
    setPreviewStrikePrice(effectivePrice);
    setNearestValidStrike(nearest);
  };

  const clearPreviewStrike = () => {
    setPreviewStrikePrice(null);
    setPreviewRawPrice(null);
    setPreviewRawY(null);
    setNearestValidStrike(null);
  };

  const handleXFromChange = (factor: number) => {
    if (typeof factor !== 'number' || factor === 1 || !isFinite(factor)) {
      return;
    }

    const currentTime = Date.now();
    const currentTimeSpan = currentTime - xFrom;

    // Calculate new time span
    const newTimeSpan = currentTimeSpan / factor;

    // Calculate limits based on available data
    const availableTimeSpan =
      priceHistory.length > 1
        ? priceHistory[priceHistory.length - 1].timestamp -
          priceHistory[0].timestamp
        : 600000;

    // Minimum: at least 30 seconds (much smaller to allow zooming in)
    const minTimeSpan = 30000; // 30 seconds

    // Maximum: show all available data plus some buffer
    const maxTimeSpan = Math.min(availableTimeSpan * 1.1, 24 * 60 * 60 * 1000); // Max 24 hours

    // Clamp the new time span
    const clampedTimeSpan = Math.min(
      Math.max(newTimeSpan, minTimeSpan),
      maxTimeSpan,
    );

    // Update xFrom (anchor zoom to current time - right edge)
    const newXFrom = currentTime - clampedTimeSpan;
    setXFrom(newXFrom);
  };

  const handleYZoomChange = (factor: number, centerPrice?: number) => {
    if (typeof factor !== 'number' || factor === 1 || !isFinite(factor)) {
      return;
    }

    const [currentMin, currentMax] = yScale;
    const currentRange = currentMax - currentMin;

    // If user has manually panned, zoom around the current view center
    // Otherwise use provided center, selected strike, or spot price
    let zoomCenter: number;
    if (hasManuallyPanned && !centerPrice) {
      // Zoom around the center of the current view
      zoomCenter = (currentMin + currentMax) / 2;
    } else {
      zoomCenter =
        centerPrice ??
        (strikePrice && strikePrice > 0 ? strikePrice : spotPrice);
    }

    // Apply zoom factor
    let newRange = currentRange / factor;

    // Calculate zoom limits based on strike spacing
    if (strikePrices.length > 1) {
      // Calculate strike spacing
      const sortedStrikes = [...strikePrices].sort((a, b) => a - b);
      const spacings = [];
      for (let i = 1; i < Math.min(5, sortedStrikes.length); i++) {
        spacings.push(sortedStrikes[i] - sortedStrikes[i - 1]);
      }
      const avgSpacing = spacings.reduce((a, b) => a + b, 0) / spacings.length;

      // Maximum range is 8 strikes (4 on each side of spot price)
      const maxRange = avgSpacing * 20;

      // Minimum range is 0.5 strikes (very zoomed in)
      const minRange = avgSpacing * 0.5;

      // Clamp the new range between min and max
      newRange = Math.min(Math.max(newRange, minRange), maxRange);
    }

    const newMin = zoomCenter - newRange / 2;
    const newMax = zoomCenter + newRange / 2;

    // Set both current and target immediately for responsive zoom
    const newScale: [number, number] = [newMin, newMax];
    setYScale(newScale);
    setTargetYScale(newScale);
  };

  const handlePan = (deltaX: number, deltaY: number) => {
    // Mark that user has manually panned to disable auto-centering
    setHasManuallyPanned(true);

    // Handle vertical pan (Y axis)
    if (Math.abs(deltaY) > 0) {
      const [currentMin, currentMax] = yScale;
      const currentRange = currentMax - currentMin;

      // Convert pixel delta to price delta
      // Assuming typical chart height of 600px
      const pricePerPixel = currentRange / 600;
      // Negate deltaY so dragging down shows higher prices (natural feeling)
      const priceDelta = -deltaY * pricePerPixel;

      const newMin = currentMin + priceDelta;
      const newMax = currentMax + priceDelta;

      const newScale: [number, number] = [newMin, newMax];
      setYScale(newScale);
      setTargetYScale(newScale);
    }

    // Handle horizontal pan (X axis)
    if (Math.abs(deltaX) > 0) {
      const currentTime = Date.now();
      const currentTimeSpan = currentTime - xFrom;

      // Convert pixel delta to time delta
      // Chart is full width of the container
      const timePerPixel = currentTimeSpan / window.innerWidth;
      const timeDelta = deltaX * timePerPixel;

      const newXFrom = xFrom + timeDelta;

      // Don't pan into the future
      if (newXFrom < currentTime - 30000) {
        // Keep at least 30 seconds visible
        setXFrom(newXFrom);
      }
    }
  };

  const recenterView = () => {
    // Reset to default view
    const defaultScale = getDefaultYScale(spotPrice, strikePrices, strikePrice);
    setTargetYScale(defaultScale);

    // Reset horizontal view to 10 minutes
    setXFrom(Date.now() - 600000);

    // Re-enable auto-centering
    setHasManuallyPanned(false);
  };

  return {
    // State
    dimensions,
    setDimensions,
    xFrom,
    setXFrom,
    createXScale,
    yScale,
    setYScale,
    previewStrikePrice,
    previewRawPrice,
    previewRawY,
    nearestValidStrike,
    smoothedCenterPrice,
    setSmoothedCenterPrice,
    smoothedSpotPrice,
    setSmoothedSpotPrice,
    smoothedYBounds,
    setSmoothedYBounds,
    isInitialized,
    setIsInitialized,
    lastAutoScaleStrike,
    setLastAutoScaleStrike,
    savedView,
    setSavedView,
    hasManuallyPanned,

    // Refs
    lastTouchDistance,
    lastUpdateTime,
    isDraggingStrike,
    yScaleRef,
    marginRef,
    touchStartTime,

    // Methods
    updatePreviewStrike,
    clearPreviewStrike,
    handleXFromChange,
    handleYZoomChange,
    handlePan,
    recenterView,
  };
}
